import type {
  HoroscopeScope,
  MutagenId,
  NormalizedChart,
  NormalizedHoroscope,
  NormalizedHoroscopeItem,
  PalaceId,
} from './domain.js';
import { buildTransformationEvidenceViewModel, MUTAGEN_LABELS_ZH_TW } from './transformationEvidence.js';

export interface PalaceStarEvidenceViewModel {
  id: string;
  label: string;
  category: string;
  brightness: string;
  birthYearMutagens: readonly { id: MutagenId; label: string }[];
}

export interface RelatedPalaceEvidenceViewModel {
  palaceId: PalaceId;
  palaceLabel: string;
  relation: 'opposite' | 'trine' | 'adjacent' | 'derived' | 'same';
  starLabels: readonly string[];
}

export interface PalaceEvidenceViewModel {
  palaceId: PalaceId;
  palaceLabel: string;
  heavenlyStem: string;
  earthlyBranch: string;
  isBodyPalace: boolean;
  stars: readonly PalaceStarEvidenceViewModel[];
  relatedPalaces: readonly RelatedPalaceEvidenceViewModel[];
  birthYearTransformations: ReturnType<typeof buildTransformationEvidenceViewModel>['birthYear'];
  outgoingPalaceStemFlights: ReturnType<typeof buildTransformationEvidenceViewModel>['palaceStemFlights'];
  incomingPalaceStemFlights: ReturnType<typeof buildTransformationEvidenceViewModel>['palaceStemFlights'];
  disclosure: string;
}

function allPalaceStars(chart: NormalizedChart, palaceId: PalaceId) {
  const palace = chart.palaces[palaceId];
  return [...palace.majorStars, ...palace.minorStars, ...palace.adjectiveStars];
}

export function buildPalaceEvidenceViewModel(chart: NormalizedChart, palaceId: PalaceId): PalaceEvidenceViewModel {
  const palace = chart.palaces[palaceId];
  if (!palace) throw new Error(`Unknown palace ${palaceId}`);
  const stars = allPalaceStars(chart, palaceId)
    .map((star) => ({
      id: star.id,
      label: star.label,
      category: star.category,
      brightness: star.brightness,
      birthYearMutagens: star.mutagens
        .map((mutagen) => ({ id: mutagen, label: MUTAGEN_LABELS_ZH_TW[mutagen] }))
        .sort((a, b) => a.id.localeCompare(b.id)),
    }))
    .sort((a, b) => a.category.localeCompare(b.category) || a.id.localeCompare(b.id));

  const relatedPalaces = chart.relations
    .filter((edge) => edge.from === palaceId && edge.to !== palaceId)
    .map((edge) => ({
      palaceId: edge.to,
      palaceLabel: chart.palaces[edge.to].label,
      relation: edge.type,
      starLabels: allPalaceStars(chart, edge.to).map((star) => star.label).sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.relation.localeCompare(b.relation) || a.palaceId.localeCompare(b.palaceId));

  return {
    palaceId,
    palaceLabel: palace.label,
    heavenlyStem: palace.heavenlyStem,
    earthlyBranch: palace.earthlyBranch,
    isBodyPalace: palace.isBodyPalace,
    stars,
    relatedPalaces,
    birthYearTransformations: buildTransformationEvidenceViewModel(chart, { kinds: ['birth_year'], targetPalaceId: palaceId }).birthYear,
    outgoingPalaceStemFlights: buildTransformationEvidenceViewModel(chart, { kinds: ['palace_stem_flight'], sourcePalaceId: palaceId }).palaceStemFlights,
    incomingPalaceStemFlights: buildTransformationEvidenceViewModel(chart, { kinds: ['palace_stem_flight'], targetPalaceId: palaceId }).palaceStemFlights,
    disclosure: '本區只把此宮的星曜、亮度、生年四化、三方四正與宮干飛化整理成可核對 Evidence；不將單一項目直接換算成吉凶分數。',
  };
}

export type TimingScope = Exclude<HoroscopeScope, 'natal'>;

export interface HoroscopeTransformationViewModel {
  mutagen: MutagenId;
  mutagenLabel: string;
  starId: string;
  starLabel: string;
  basis: 'scope_heavenly_stem' | 'palace_heavenly_stem';
}

export interface HoroscopePalaceEvidenceViewModel {
  natalPalaceId: PalaceId;
  natalPalaceLabel: string;
  cyclePalaceId?: PalaceId;
  cyclePalaceLabel?: string;
  movingStarLabels: readonly string[];
}

export interface HoroscopeScopeEvidenceViewModel {
  scope: TimingScope;
  name: string;
  heavenlyStem: string;
  earthlyBranch: string;
  nominalAge?: number;
  transformations: readonly HoroscopeTransformationViewModel[];
  palaces: readonly HoroscopePalaceEvidenceViewModel[];
  disclosure: string;
}

function horoscopeItem(horoscope: NormalizedHoroscope, scope: TimingScope): NormalizedHoroscopeItem {
  return horoscope[scope];
}

export function buildHoroscopeScopeEvidenceViewModel(
  chart: NormalizedChart,
  horoscope: NormalizedHoroscope,
  scope: TimingScope,
): HoroscopeScopeEvidenceViewModel {
  if (chart.calculation.chartFingerprint !== horoscope.natalChartFingerprint) {
    throw new Error('Horoscope evidence view rejected a different natal chart fingerprint');
  }
  const item = horoscopeItem(horoscope, scope);
  const transformations = item.mutagens
    .map((entry) => ({
      mutagen: entry.mutagen,
      mutagenLabel: MUTAGEN_LABELS_ZH_TW[entry.mutagen],
      starId: entry.starId,
      starLabel: entry.starLabel,
      basis: entry.basis,
    }))
    .sort((a, b) => a.mutagen.localeCompare(b.mutagen) || a.starId.localeCompare(b.starId));
  const palaces = chart.palaceOrder.map((natalPalaceId) => {
    const cyclePalaceId = item.cyclePalaceByNatalPalace[natalPalaceId];
    return {
      natalPalaceId,
      natalPalaceLabel: chart.palaces[natalPalaceId].label,
      ...(cyclePalaceId ? { cyclePalaceId, cyclePalaceLabel: chart.palaces[cyclePalaceId].label } : {}),
      movingStarLabels: (item.movingStarsByNatalPalace[natalPalaceId] ?? []).map((star) => star.label).sort((a, b) => a.localeCompare(b)),
    };
  });
  return {
    scope,
    name: item.name,
    heavenlyStem: item.heavenlyStem,
    earthlyBranch: item.earthlyBranch,
    ...(item.nominalAge === undefined ? {} : { nominalAge: item.nominalAge }),
    transformations,
    palaces,
    disclosure: scope === 'minor'
      ? '小限保留虛歲、宮位映射與宮干衍生四化基礎；不偽裝成具有獨立流耀集合。'
      : `${scope} 層只呈現該時間層的干支、十二宮映射、四化與流耀 Evidence；尚未觸發已驗證規則時不產生運勢結論。`,
  };
}
